import React, { useState, useMemo } from "react";
import {
    Box,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Select,
    Heading,
    useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import { last, groupBy } from "lodash";
import DealCalculations from "./DealCalculations";
import {
    Page,
    Text as PdfText,
    View,
    Document,
    StyleSheet,
    PDFDownloadLink,
} from "@react-pdf/renderer";

const CurrencyFormatter = (value) => {
    return new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(value);
};

// PDF Styles
const styles = StyleSheet.create({
    page: { padding: 30, fontSize: 10, fontFamily: "Helvetica" },
    header: { fontSize: 16, marginBottom: 20, fontWeight: "bold", textAlign: "center" },
    table: { display: "table", width: "auto", borderStyle: "solid", borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0 },
    tableRow: { margin: "auto", flexDirection: "row" },
    tableCol: { width: "20%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableColWide: { width: "40%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
    tableCell: { margin: "auto", marginTop: 5, marginBottom: 5, padding: 2 },
    tableHeader: { fontWeight: "bold" },
    summaryBox: { marginTop: 20, padding: 10, border: 1, flexDirection: "row", justifyContent: "space-between" },
});

// PDF Component
const AgentReportPDF = ({ agentSummary, deals, year }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View>
                <PdfText style={styles.header}>Agent Deals Report - {agentSummary.name} ({year})</PdfText>
            </View>

            <View style={styles.summaryBox}>
                <View><PdfText>Total Earning: {CurrencyFormatter(agentSummary.totalEarning)}</PdfText></View>
                <View><PdfText>HST (13%): {CurrencyFormatter(agentSummary.totalHST)}</PdfText></View>
                <View><PdfText>Total (Inc. HST): {CurrencyFormatter(agentSummary.totalEarning + agentSummary.totalHST)}</PdfText></View>
            </View>

            <View style={{ marginTop: 20 }}>
                <PdfText style={{ marginBottom: 10, fontWeight: "bold", fontSize: 12 }}>Deal Breakdown</PdfText>
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}><PdfText style={[styles.tableCell, styles.tableHeader]}>Date</PdfText></View>
                        <View style={styles.tableColWide}><PdfText style={[styles.tableCell, styles.tableHeader]}>Address</PdfText></View>
                        <View style={styles.tableCol}><PdfText style={[styles.tableCell, styles.tableHeader]}>Deal ID</PdfText></View>
                        <View style={styles.tableCol}><PdfText style={[styles.tableCell, styles.tableHeader]}>Earning</PdfText></View>
                        <View style={styles.tableCol}><PdfText style={[styles.tableCell, styles.tableHeader]}>HST</PdfText></View>
                    </View>
                    {deals.map((row, idx) => (
                        <View style={styles.tableRow} key={idx}>
                            <View style={styles.tableCol}><PdfText style={styles.tableCell}>{moment(row.date).format("ll")}</PdfText></View>
                            <View style={styles.tableColWide}><PdfText style={styles.tableCell}>{row.address}</PdfText></View>
                            <View style={styles.tableCol}><PdfText style={styles.tableCell}>{row.dealId}</PdfText></View>
                            <View style={styles.tableCol}><PdfText style={styles.tableCell}>{CurrencyFormatter(row.earning)}</PdfText></View>
                            <View style={styles.tableCol}><PdfText style={styles.tableCell}>{CurrencyFormatter(row.hst)}</PdfText></View>
                        </View>
                    ))}
                </View>
            </View>
        </Page>
    </Document>
);


const DealsReport = ({ deals, allAgents }) => {
    const years = useMemo(() => {
        const yearsSet = new Set(
            deals.map((deal) => moment(deal.closing_date).year())
        );
        return Array.from(yearsSet).sort((a, b) => b - a);
    }, [deals]);

    const [selectedYear, setSelectedYear] = useState(
        years.length > 0 ? years[0] : moment().year()
    );

    const [selectedAgent, setSelectedAgent] = useState(null);

    const reportData = useMemo(() => {
        const yearDeals = deals.filter(
            (deal) => moment(deal.closing_date).year() === parseInt(selectedYear)
        );

        const agentSummaries = {};
        const dealBreakdown = [];

        yearDeals.forEach((deal) => {
            const calculations = DealCalculations(deal, allAgents);
            const { agentComissions, HST } = calculations;

            Object.keys(agentComissions).forEach((agentId) => {
                const agent = allAgents.find((a) => a._id === agentId);
                const agentName = agent ? agent.name : agentId;
                const earning = agentComissions[agentId];
                const hstAmount = earning * HST;

                if (!agentSummaries[agentId]) {
                    agentSummaries[agentId] = {
                        id: agentId,
                        name: agentName,
                        totalEarning: 0,
                        totalHST: 0,
                    };
                }

                agentSummaries[agentId].totalEarning += earning;
                agentSummaries[agentId].totalHST += hstAmount;

                dealBreakdown.push({
                    date: deal.closing_date,
                    dealId: deal.deal_id,
                    address: deal.address,
                    agentId: agentId,
                    agent: agentName,
                    earning: earning,
                    hst: hstAmount,
                });
            });
        });

        return {
            summaries: Object.values(agentSummaries).sort((a, b) => b.totalEarning - a.totalEarning),
            breakdown: dealBreakdown.sort((a, b) => moment(b.date).diff(moment(a.date))),
        };
    }, [selectedYear, deals, allAgents]);

    const filteredBreakdown = useMemo(() => {
        if (!selectedAgent) return reportData.breakdown;
        return reportData.breakdown.filter(row => row.agentId === selectedAgent);
    }, [selectedAgent, reportData.breakdown]);

    const selectedAgentSummary = useMemo(() => {
        if (!selectedAgent) return null;
        return reportData.summaries.find(s => s.id === selectedAgent);
    }, [selectedAgent, reportData.summaries]);

    const textColor = useColorModeValue("gray.700", "white");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("gray.100", "gray.700");
    const selectedBg = useColorModeValue("blue.50", "blue.900");

    return (
        <Box p="4">
            <Flex direction="column" mb="8">
                <Flex align="center" justify="space-between" mb="4">
                    <Flex align="center">
                        <Text fontWeight="bold" mr="4">
                            Select Year:
                        </Text>
                        <Select
                            width="150px"
                            value={selectedYear}
                            onChange={(e) => {
                                setSelectedYear(e.target.value);
                                setSelectedAgent(null); // Reset selection on year change
                            }}
                        >
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                    {selectedAgent && selectedAgentSummary && (
                        <PDFDownloadLink
                            document={<AgentReportPDF agentSummary={selectedAgentSummary} deals={filteredBreakdown} year={selectedYear} />}
                            fileName={`DealsReport_${selectedAgentSummary.name.replace(/\s+/g, '_')}_${selectedYear}.pdf`}
                        >
                            {({ loading }) => (
                                <Box
                                    as="button"
                                    px="4"
                                    py="2"
                                    bg="teal.500"
                                    color="white"
                                    fontWeight="bold"
                                    borderRadius="md"
                                    _hover={{ bg: "teal.600" }}
                                >
                                    {loading ? "Preparing PDF..." : "Download PDF"}
                                </Box>
                            )}
                        </PDFDownloadLink>
                    )}
                </Flex>

                <Heading size="md" mb="2">
                    Agent Summary ({selectedYear})
                </Heading>
                <Text fontSize="sm" color="gray.500" mb="4">Click on an agent row to view their specific deals and download a PDF.</Text>
                <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" mb="8">
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Agent</Th>
                                <Th isNumeric>Total Earning</Th>
                                <Th isNumeric>Total HST (13%)</Th>
                                <Th isNumeric>Total Inc. HST</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {reportData.summaries.map((summary) => {
                                const isSelected = selectedAgent === summary.id;
                                return (
                                    <Tr
                                        key={summary.id}
                                        cursor="pointer"
                                        onClick={() => setSelectedAgent(isSelected ? null : summary.id)}
                                        bg={isSelected ? selectedBg : "transparent"}
                                        _hover={{ bg: isSelected ? selectedBg : hoverBg }}
                                    >
                                        <Td fontWeight="bold">{summary.name}</Td>
                                        <Td isNumeric>{CurrencyFormatter(summary.totalEarning)}</Td>
                                        <Td isNumeric>{CurrencyFormatter(summary.totalHST)}</Td>
                                        <Td isNumeric>{CurrencyFormatter(summary.totalEarning + summary.totalHST)}</Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>

                <Heading size="md" mb="4">
                    Deal Breakdown {selectedAgentSummary ? `- ${selectedAgentSummary.name}` : ""}
                </Heading>
                <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" maxH="500px">
                    <Table variant="simple">
                        <Thead position="sticky" top={0} bg={useColorModeValue("white", "gray.800")} zIndex={1}>
                            <Tr>
                                <Th>Date</Th>
                                <Th>Deal ID</Th>
                                <Th>Address</Th>
                                <Th>Agent</Th>
                                <Th isNumeric>Earning</Th>
                                <Th isNumeric>HST</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredBreakdown.map((row, idx) => (
                                <Tr key={`${row.dealId}-${idx}`}>
                                    <Td>{moment(row.date).format("ll")}</Td>
                                    <Td>{row.dealId}</Td>
                                    <Td>{row.address}</Td>
                                    <Td>{row.agent}</Td>
                                    <Td isNumeric>{CurrencyFormatter(row.earning)}</Td>
                                    <Td isNumeric>{CurrencyFormatter(row.hst)}</Td>
                                </Tr>
                            ))}
                            {filteredBreakdown.length === 0 && (
                                <Tr>
                                    <Td colSpan={6} textAlign="center" py="4">No deals found for this selection.</Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Flex>
        </Box>
    );
};

export default DealsReport;
